import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { submitRequest } from '../api/Request.client';
import { uploadFiles } from '../api/Upload.client';
import { useRequests } from '../contexts/RequestsContext';
import { AttachStatus } from '../containers/Request/RequestForm';
import {
  ILabel,
  ILabelResponse,
  IRequestType,
  IUploadedFile,
} from '../constants/interfaces';
import { ISnackbarState } from '../components/ValidationModals';
import { OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID } from '../constants/app';
import { FormState } from './useFormState';

interface ApiError {
  response?: {
    data?: string;
  };
  message?: string;
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null;
}

class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadError';
  }
}

interface UseRequestFormSubmitParams {
  requestType: IRequestType;
  visibleFields: ILabel[];
  formState: FormState;
  setSelectedFiles: (files: File[]) => void;
  selectedFiles: File[];
  setSnackbar: (state: ISnackbarState) => void;
  setLoading: (val: boolean) => void;
  setErrors: (errors: unknown) => void;
  clearFormData: () => void;
  attachmentStatus: AttachStatus;
}

export function useRequestFormSubmit({
  requestType,
  visibleFields,
  formState,
  setSelectedFiles,
  selectedFiles,
  setSnackbar,
  setLoading,
  setErrors,
  clearFormData,
  attachmentStatus,
}: UseRequestFormSubmitParams) {
  const [uploadErrorText, setUploadErrorText] = useState<string>('');
  const { invalidateRequestsListCache } = useRequests();

  const mutation = useMutation({
    mutationFn: async () => {
      const account_id = String(formState[2]?.value ?? '');
      // The server uploads to S3/RustFS itself and hands back the URL-free record; nothing
      // here ever sees a presigned URL to strip before persisting it with the request.
      let uploadedFiles: IUploadedFile[] = [];

      if (selectedFiles.length > 0) {
        try {
          uploadedFiles = await uploadFiles(selectedFiles, {
            account_id,
            folder_path: requestType.folder_path || '',
          });
          setSnackbar({
            message: 'Files uploaded successfully!',
            open: true,
            severity: 'success',
          });
        } catch (uploadError: unknown) {
          const apiError = isApiError(uploadError) ? uploadError : {};
          const apiMessage =
            apiError.response?.data ||
            apiError.message ||
            'File operation failed.';
          throw new UploadError(apiMessage);
        }
      }

      const labelResponses = visibleFields
        .filter((field: { labelId: number }) => {
          const value = formState[field.labelId]?.value;
          return value !== undefined && value !== null && value !== '';
        })
        .map((field: { labelId: number }) => ({
          childResponses: (formState[field.labelId].children ||
            []) as ILabelResponse[],
          labelId: field.labelId,
          response: String(formState[field.labelId].value),
        }));

      if (requestType.name === 'Property Preservation') {
        const auxKey = String(OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID);
        const otherDetailsValue = formState[auxKey]?.value;
        if (
          otherDetailsValue !== undefined &&
          otherDetailsValue !== null &&
          otherDetailsValue !== ''
        ) {
          const existingAux = labelResponses.find(
            (lr: { labelId: number }) =>
              lr.labelId === OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID
          );
          if (!existingAux) {
            labelResponses.push({
              childResponses: (formState[auxKey].children ||
                []) as ILabelResponse[],
              labelId: OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID,
              response: String(otherDetailsValue),
            });
          }
        }
      }

      await submitRequest({
        accountId: account_id,
        labelResponses,
        preSignedUrls: uploadedFiles,
        requestTypeId: requestType.id,
      });
    },
    onError: (error: unknown) => {
      if (error instanceof UploadError) {
        setSnackbar({
          message: `File upload failed: ${error.message}`,
          open: true,
          severity: 'error',
        });
      } else {
        const apiError = isApiError(error) ? error : {};
        const apiMessage =
          apiError.response?.data ||
          apiError.message ||
          'Request submission failed. Please try again.';
        setSnackbar({
          message: `Request submission failed: ${apiMessage}`,
          open: true,
          severity: 'error',
        });
      }
    },
    onMutate: () => setLoading(true),
    onSettled: () => setLoading(false),
    onSuccess: () => {
      invalidateRequestsListCache();
      setSnackbar({
        message: 'Servicer Request Submitted successfully!',
        open: true,
        severity: 'success',
      });
      clearFormData();
    },
  });

  const handleSubmit = () => {
    if (
      attachmentStatus === AttachStatus.Required &&
      selectedFiles.length === 0
    ) {
      setUploadErrorText('Attachments are required to create servicer request');
      return;
    }

    if (
      attachmentStatus === AttachStatus.NotAllowed &&
      selectedFiles.length > 0
    ) {
      setUploadErrorText('');
      if (typeof setSelectedFiles === 'function') {
        setSelectedFiles([]);
      }
      setSnackbar({
        message:
          'Attachments are not allowed based on your responses. All attached files have been discarded.',
        open: true,
        severity: 'warning',
      });
      return;
    }

    mutation.mutate();
  };

  return { handleSubmit, setUploadErrorText, uploadErrorText };
}
